import { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowClockwiseIcon, BookOpenTextIcon, PresentationChartIcon, SparkleIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@libs/zapi-ui/ds/components/empty";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { WidgetCard, WidgetCardHeader } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/WidgetCard";
import {
  SECTION_STICKY_GAP,
  sectionStickyTop
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/SectionPage";

export type TDeckState = "loading" | "generating" | "empty" | "ready";

type TPresentationBuilderProps = {
  deckState: TDeckState;
  deckUpdating: boolean;
  deckMeta: string | null;
  canEdit: boolean;
  showInstructions: boolean;
  onOpenInstructions: () => void;
  onCreateSlides: () => void;
  onRefreshSlides: (() => void) | null;
  preview: ReactNode;
};

// The deck fills the window under the pinned card header.
const BODY_HEIGHT = "h-[calc(100svh-var(--app-header-height)-11rem)] min-h-[30rem]";

export function PresentationBuilder({
  deckState,
  deckUpdating,
  deckMeta,
  canEdit,
  showInstructions,
  onOpenInstructions,
  onCreateSlides,
  onRefreshSlides,
  preview
}: TPresentationBuilderProps) {
  const { t } = useTranslation(["projectHome", "common"]);
  const isReady = deckState === "ready";

  const actions = isReady && (
    <>
      {showInstructions && (
        <Button
          variant="ghost"
          size="icon-sm"
          shape="pill"
          aria-label={t("presentationBuilder.outlineInstructions")}
          title={t("presentationBuilder.outlineInstructions")}
          onClick={onOpenInstructions}
          data-testid="presentation-outline-instructions-button"
          className="text-muted-foreground"
        >
          <BookOpenTextIcon />
        </Button>
      )}
      {deckUpdating && (
        <span
          className="typo-caption flex items-center gap-1.5 text-muted-foreground"
          data-testid="presentation-slides-updating"
        >
          <Spinner aria-label={t("presentationBuilder.updatingSlides")} className="size-3.5" />
          {t("presentationBuilder.updatingSlides")}
        </span>
      )}
      {canEdit && onRefreshSlides && (
        <Button
          variant="outline"
          size="sm"
          shape="pill"
          onClick={onRefreshSlides}
          data-testid="presentation-refresh-slides"
        >
          <ArrowClockwiseIcon data-icon="inline-start" />
          {t("presentationBuilder.refreshSlides")}
        </Button>
      )}
    </>
  );

  const body =
    deckState === "generating" ? (
      <div className="flex min-h-[26rem] flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <Spinner aria-label={t("common:loading")} className="size-6 text-primary-text" />
        <h3 className="typo-h5 text-foreground">{t("presentationBuilder.creatingSlides")}</h3>
        <p className="typo-body-sm max-w-[40ch] text-muted-foreground">
          {t("presentationBuilder.creatingDescription")}
        </p>
      </div>
    ) : deckState === "empty" ? (
      <Empty className="py-12">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <PresentationChartIcon weight="duotone" />
          </EmptyMedia>
          <EmptyTitle>{t("presentationBuilder.emptyTitle")}</EmptyTitle>
          <EmptyDescription>{t("presentationBuilder.emptyDescription")}</EmptyDescription>
        </EmptyHeader>
        {canEdit && (
          <EmptyContent className="flex-row flex-wrap justify-center gap-2">
            {showInstructions && (
              <Button
                variant="outline"
                shape="pill"
                onClick={onOpenInstructions}
                data-testid="outline-empty-instructions-button"
              >
                <BookOpenTextIcon data-icon="inline-start" />
                {t("presentationBuilder.outlineInstructions")}
              </Button>
            )}
            <Button shape="pill" onClick={onCreateSlides} data-testid="presentation-create-slides">
              <SparkleIcon data-icon="inline-start" weight="fill" />
              {t("presentationBuilder.createSlides")}
            </Button>
          </EmptyContent>
        )}
      </Empty>
    ) : (
      <div className={`flex justify-center overflow-hidden rounded-b-2xl bg-muted/40 p-4 ${BODY_HEIGHT}`}>{preview}</div>
    );

  return (
    <section
      data-testid="presentation-page"
      style={{ "--widget-sticky-gap": SECTION_STICKY_GAP } as CSSProperties}
      className="mx-auto flex w-full max-w-[960px] flex-col pb-8"
    >
      <h1 className="typo-h3 mb-6 min-h-10 text-foreground">{t("presentationBuilder.pageTitle")}</h1>
      <WidgetCard data-testid="presentation-builder">
        <WidgetCardHeader
          title={t("presentationBuilder.title")}
          titleAdornment={
            deckMeta && isReady ? (
              <span className="typo-caption hidden shrink-0 text-muted-foreground sm:inline">{deckMeta}</span>
            ) : undefined
          }
          stickyTop={sectionStickyTop(false)}
          actions={actions}
        />
        {body}
      </WidgetCard>
    </section>
  );
}
